import { type HttpClientConfig, type HttpClient } from 'core/infra/http/HttpClient'
import { type StorageInterface } from 'core/infra/storage/StorageInterface'

import { AGNOSTIC_STRONG_AUTH_TOKEN_KEY } from '../domain/User'

type AgnosticStrongAuthRepositoryDependencies = {
  readonly httpClient: HttpClient
  readonly storage: Pick<StorageInterface, 'get' | 'set'>
}

export type AuthenticationOptions = {
  entityIdentifier: string
}

type AuthenticationResponse = {
  access: string
}

/**
 * The reasoaning behind is:
 * A carrier that doesn't have an account receives a 'magic link' in the email.
 * With that 'magic link' they can get a token to access some specific resources.
 * But when they are requesting this token, we validate if they can really access that resource.
 * In the case of the rate, we check if that rate is from the carrier that received that magic link
 */
export class AgnosticStrongAuthRepository {
  private httpClient: HttpClient
  private storage: Pick<StorageInterface, 'get' | 'set'>

  constructor({ httpClient, storage }: AgnosticStrongAuthRepositoryDependencies) {
    this.httpClient = httpClient
    this.storage = storage
  }

  getLocalToken() {
    return this.storage.get<string>(AGNOSTIC_STRONG_AUTH_TOKEN_KEY)
  }

  setLocalToken(token: string) {
    this.storage.set(AGNOSTIC_STRONG_AUTH_TOKEN_KEY, token)
  }

  async fetchAccessToken(
    { entityIdentifier }: AuthenticationOptions,
    config?: Partial<HttpClientConfig>
  ): Promise<AuthenticationResponse['access']> {
    const endpoint = `/auth/carriers/rate/${entityIdentifier}/token`
    const response = await this.httpClient.post<AuthenticationResponse>(endpoint, null, config)
    const token = response.data.access

    return token
  }
}
